import PrimaryCard from "../common/cards/PrimaryCard";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useChat } from "../../hooks";
import {
  Box,
  Card,
  CardBody,
  HStack,
  Icon,
  IconButton,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CarIcon, OperatorIcon } from "../common/icons";
import {
  FaCheck,
  FaExclamationTriangle,
} from "react-icons/fa";
import { MessageSenderType, MessageStatus } from "../../enums";
import { useTranslation } from "react-i18next";
import moment from "moment";
import ChatSuggestionsMenu from "./ChatSuggestionsMenu";
import SendIcon from "../common/icons/SendIcon";

const ChatCard = () => {
  const [containerHeight, setContainerHeight] = useState(0);
  const [inputHeight, setInputHeight] = useState(0);
  const { t } = useTranslation();
  const { messages, onMessageSend } = useChat();
  const [message, setMessage] = useState("");
  const chatRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const cardRef = useRef(null);

  const onMessageChange = useCallback((event) => {
    setMessage(event.target.value);
  }, []);

  const onSendClick = useCallback(() => {
    if (message) {
      onMessageSend(message);
      setMessage("");
    }
  }, [message, onMessageSend]);

  const onKeyDown = useCallback(
    (event) => {
      if (event.keyCode === 13) {
        onSendClick();
      }
    },
    [onSendClick]
  );

  const onSelectSuggestion = useCallback((suggestion) => {
    setMessage(suggestion);
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (containerRef.current) {
      setContainerHeight(containerRef.current.offsetHeight);
    }
    if (inputRef.current) {
      setInputHeight(inputRef.current.offsetHeight);
    }
  }, []);

  const messagesHeight = useMemo(
    () => containerHeight - inputHeight - 16,
    [containerHeight, inputHeight],
  );

  return (
    <PrimaryCard title={t("plugin.chat.title")}>
      <VStack
        ref={containerRef}
        position={"relative"}
        minWidth={"350px"}
        height={"full"}
        justifyContent={"flex-end"}
      >
        <VStack
          position={"absolute"}
          width={"full"}
          overflowY={"auto"}
          top={0}
          left={0}
          ref={chatRef}
          maxHeight={`${messagesHeight}px`}
        >
          {messages.map(
            ({ Content, Sender, Status, CreatedTimestamp }, index) => {
              return (
                <HStack
                  width={"full"}
                  alignContent={"stretch"}
                  key={Sender?.Name + Content + index}
                  flexDirection={
                    Sender?.Name === MessageSenderType.OPERATOR
                      ? "row-reverse"
                      : "row"
                  }
                  justifyContent={"stretch"}
                >
                  <Box
                  backgroundColor={
                    Sender?.Name === MessageSenderType.OPERATOR
                      ? "#8BAFC288"
                      : "#5B5F6188"
                  }
                  borderRadius={"full"}
                  padding={"8px"}
                  >
                    <Icon
                      as={
                        Sender?.Name === MessageSenderType.OPERATOR
                          ? OperatorIcon
                          : CarIcon
                      }
                      boxSize={"28px"}
                    />
                  </Box>
                  <Card
                    backgroundColor={
                      Sender?.Name === MessageSenderType.OPERATOR
                        ? "#8BAFC288"
                        : "#5B5F6188"
                    }
                    borderRadius={"7px"}
                    maxWidth={"80%"}
                    flexGrow={1}
                    margin={0}
                  >
                    <CardBody margin={2} padding={0}>
                      <VStack alignItems={"stretch"}>
                        <Text
                          fontSize={"md"}
                          textAlign={"start"}
                          wordBreak={"break-word"}
                        >
                          {Content}
                        </Text>
                        <HStack gap={1} justifyContent={"flex-end"}>
                          <Text fontSize={"xs"}>
                            {moment(CreatedTimestamp).format("HH:mm")}
                          </Text>
                          {Sender?.Name === MessageSenderType.OPERATOR && (
                            <Text fontSize={"xs"}>
                              <Icon
                                as={
                                  Status?.Value === MessageStatus.SENT
                                    ? FaCheck
                                    : FaExclamationTriangle
                                }
                              />
                            </Text>
                          )}
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>
                </HStack>
              );
            },
          )}
        </VStack>
        <Card backgroundColor={"#84848480"} borderRadius={"30px"} zIndex={200} width={"full"} ref={cardRef}>
          <CardBody padding={"5px"}>
            <HStack width={"full"} padding={1} ref={inputRef}>
              <ChatSuggestionsMenu onSelectSuggestion={onSelectSuggestion} width={(cardRef.current?.offsetWidth - 25) + "px"} />
              <Input
                flexGrow={1}
                borderRadius={"full"}
                color={"white"}
                borderColor={"transparent"}
                backgroundColor={"#F1F1F177"}
                value={message}
                onChange={onMessageChange}
                onKeyDown={onKeyDown}
                placeholder={t("plugin.chat.placeholder")}
                size={"md"}
                _placeholder={{
                  color: "white"
                }}
                _hover={{
                  borderColor: "#5B5F6188"
                }}
              />
              <IconButton
                onClick={onSendClick}
                color={"white"}
                size={"md"}
                borderRadius={"full"}
                backgroundColor={"#F1F1F177"}
                borderWidth={"1px"}
                borderColor={"#F1F1F177"}
                _hover={{ backgroundColor: "#5B5F6177" }}
                _active={{ backgroundColor: "#5B5F6177" }}
                icon={<Icon as={SendIcon} boxSize={"20px"} />}
              />
            </HStack>
          </CardBody>
        </Card>
      </VStack>
    </PrimaryCard>
  );
};

export default React.memo(ChatCard);
